import $ from 'jquery';
import '@classic/Shared/BootstrapTooltip';
import { type PageCalendarEventProps } from './EventTransform';
import { GOOGLE_FEED_NAME } from './EventLink';
import { formatLongDate, formatTooltipTime } from './TimeFormat';
import DOMPurify from 'dompurify';
import { registerTooltipEscapeDismissal } from '@classic/Shared/TooltipEscape';

/** Strip any markup from backend-supplied text, mirroring v3's `$(html).text()`. */
const toPlainText = (value: string | null | undefined): string => {
    if (!value) {
        return '';
    }
    const el = document.createElement('div');
    el.innerHTML = value;
    return (el.textContent ?? '').trim();
};

const sourceCalendarLine = (props: PageCalendarEventProps, pageCalendarId: number): string => {
    if (props.feedSource === GOOGLE_FEED_NAME) {
        if (!props.calendarName) {
            return '';
        }
        return `<span class="fc-label">Source Calendar:</span> ${DOMPurify.sanitize(props.calendarName)}`;
    }

    const fromAnotherCalendar = props.calendarId !== pageCalendarId;
    if (!props.calendarName || !fromAnotherCalendar) {
        return '';
    }
    const name = props.calendarName.replace('Calendar: ', '');
    return `<span class="fc-label">Source Calendar:</span> ${DOMPurify.sanitize(name)}`;
};

/**
 * Build the rich hover/focus tooltip for a grid event, reproducing the v3 qTip
 * content (title, source calendar, details, location, times) from the fields
 * the API exposes.
 */
export const buildTooltipHtml = (
    props: PageCalendarEventProps,
    title: string,
    pageCalendarId: number,
): string => {
    const lines: string[] = [];

    if (title) {
        lines.push(`<span class="fc-label">Title:</span> ${DOMPurify.sanitize(title)}`);
    }

    const source = sourceCalendarLine(props, pageCalendarId);
    if (source) {
        lines.push(source);
    }

    const details = toPlainText(props.description);
    if (details) {
        lines.push(`<span class="fc-label">Details:</span> ${DOMPurify.sanitize(details)}`);
    }

    const location = toPlainText(props.address);
    if (location) {
        lines.push(`<span class="fc-label">Location:</span> ${DOMPurify.sanitize(location)}`);
    }

    const time = formatTooltipTime(props.startDatetime, props.endDatetime, props.siteTimezone, props.allDay);
    if (time) {
        const label = props.allDay ? 'Date' : 'Time';
        lines.push(`<span class="fc-label">${label}:</span> ${DOMPurify.sanitize(time)}`);
    }

    return lines.join('<br />');
};

/** Concise label announced to screen readers when an event receives focus. */
export const buildAccessibleLabel = (props: PageCalendarEventProps, title: string): string => {
    const parts: string[] = [];

    if (props.startDatetime) {
        parts.push(formatLongDate(props.startDatetime, props.siteTimezone, props.allDay));
    }
    if (title) {
        parts.push(title);
    }
    const time = formatTooltipTime(props.startDatetime, props.endDatetime, props.siteTimezone, props.allDay);
    if (time) {
        parts.push(time);
    }
    if (props.calendarName) {
        parts.push(props.calendarName);
    }

    return parts.join(', ');
};

/**
 * Attach a Bootstrap tooltip to a grid event element. Light DOM means
 * focus/hover bubble normally, so no Shadow DOM focus shims are needed.
 */
export const attachTooltip = (
    element: HTMLElement,
    htmlContent: string,
    ariaLabel: string,
): void => {
    if (!htmlContent) {
        return;
    }

    element.setAttribute('aria-label', ariaLabel);

    $(element).tooltip({
        container: 'body',
        html: true,
        placement: 'auto bottom',
        title: htmlContent,
        trigger: 'hover focus',
    });

    registerTooltipEscapeDismissal(element);
};
